import {
  createContext,
  useCallback,
  useContext,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import type { UserPreferences } from '@/api';
import { ApiError } from '@/api/http';
import { ApiErrorCodes } from '@/api/errors';
import { useApi } from '@/api/provider';
import { STALE_TIMES } from '@/api/queryClient';
import { queryKeys } from '@/api/queryKeys';
import { configureDateFormat } from '@/utils/format';

/**
 * Settings → General, for everything that reads it.
 *
 * ## Which side of the wire each setting lives on
 *
 * The rule is what a setting describes. What describes the **person** — date
 * format, time zone, reading behaviour, the signature — is stored on the
 * server (`GET`/`PUT /preferences`) and follows them to every device. What
 * describes the **screen** — theme and density (`useTheme`), where the reading
 * pane sits (`useReadingPane`, below) — stays in this browser: a dark phone
 * beside a light desktop, or a pane beside the list on a wide monitor and
 * under it on a laptop, are ordinary and must not fight each other.
 *
 * ## Why a provider
 *
 * The date format has to be applied before the first timestamp renders, in
 * every view, so the preferences are loaded once above the routed views and
 * pushed into `utils/format` from here. Anything else that consumes a setting
 * (the message list for snippets, the composer for the signature) reads it with
 * {@link usePreferences} rather than issuing a query of its own.
 */

/** What `GET /preferences` answers before anything was saved. */
export const DEFAULT_PREFERENCES: UserPreferences = {
  locale: '',
  timeZone: '',
  markReadEnabled: true,
  markReadDelaySeconds: 0,
  showSnippets: true,
  conversationView: true,
  undoSendSeconds: 5,
  signature: '',
  signatureOnReply: false,
  videoCallUrl: '',
  videoCallRoomPrefix: '',
};

export interface PreferencesContextValue {
  /** The saved preferences, or the defaults while loading or when unavailable. */
  preferences: UserPreferences;
  isLoading: boolean;
  error: Error | undefined;
  /**
   * True when this server has nowhere to keep preferences (no durable store,
   * answered `501`). Nothing is broken and no retry helps, so the settings page
   * says so instead of offering a Save button that cannot work.
   */
  unavailable: boolean;
  refetch: () => void;
  /**
   * The saved preferences (or the verdict that there are none) have arrived.
   * Absent outside a provider. Only a preference whose two values would load
   * DIFFERENT data needs it — see `useConversationView`.
   */
  decided?: boolean;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

function isUnavailable(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.is(ApiErrorCodes.NotImplemented) || error.is(ApiErrorCodes.UnsupportedByHost))
  );
}

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const query = useQuery({
    queryKey: queryKeys.preferences(),
    queryFn: ({ signal }) => api.preferences.get(signal),
    staleTime: STALE_TIMES.metadata,
    // A 501 is an answer, not a failure: retrying it only delays the page
    // saying so.
    retry: (count, error) => !isUnavailable(error) && count < 2,
  });

  const preferences = query.data ?? DEFAULT_PREFERENCES;
  // Applied during render, not in an effect, so the very first paint after the
  // preferences arrive already uses them. It is idempotent and does nothing
  // when the values have not changed.
  configureDateFormat({ locale: preferences.locale, timeZone: preferences.timeZone });

  const { refetch } = query;
  const retry = useCallback(() => void refetch(), [refetch]);
  const value: PreferencesContextValue = {
    preferences,
    isLoading: query.isPending,
    error: query.error ?? undefined,
    unavailable: isUnavailable(query.error),
    refetch: retry,
    // Answered, refused, or parked offline: whichever, the defaults are now
    // what the app runs on, so nothing waits for a better answer.
    decided: query.data !== undefined || query.isError || query.fetchStatus === 'paused',
  };

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

/**
 * The owner's preferences. Outside a provider (a component test, a popped-out
 * window) it answers the defaults, which is what the app does with no
 * preference at all — never a throw.
 */
export function usePreferences(): PreferencesContextValue {
  return (
    useContext(PreferencesContext) ?? {
      preferences: DEFAULT_PREFERENCES,
      isLoading: false,
      error: undefined,
      unavailable: false,
      refetch: () => {},
    }
  );
}

/**
 * Settings → General → "Conversation view" (ADR 0077), as the mail list needs
 * it: `undefined` while the saved choice is still on its way.
 *
 * Unlike the other preferences this one decides WHICH list is fetched — the
 * conversations or the messages — so guessing the default and correcting it a
 * moment later would send both requests and swap one list for the other under
 * the reader. The list waits instead (its skeleton is already on screen on a
 * cold start). Outside a provider — a component test, a popped-out window —
 * it is off: those render a list, if at all, exactly as they always have.
 * A server that predates the setting sends nothing, which is the default: on.
 */
export function useConversationView(): boolean | undefined {
  const context = useContext(PreferencesContext);
  if (context === null) return false;
  if (context.decided === false) return undefined;
  return context.preferences.conversationView !== false;
}

/* ---------------------------------------------------- reading-pane position */

export type ReadingPanePosition = 'right' | 'bottom' | 'off';

const READING_PANE_KEY = 'ols-mail.reading-pane';
const READING_PANE_VALUES: readonly ReadingPanePosition[] = ['right', 'bottom', 'off'];

function readReadingPane(): ReadingPanePosition {
  try {
    const value = localStorage.getItem(READING_PANE_KEY);
    return READING_PANE_VALUES.includes(value as ReadingPanePosition)
      ? (value as ReadingPanePosition)
      : 'right';
  } catch {
    return 'right';
  }
}

const readingPaneListeners = new Set<() => void>();

function emitReadingPane(): void {
  for (const listener of readingPaneListeners) listener();
}

function onReadingPaneStorage(event: StorageEvent): void {
  if (event.key === null || event.key === READING_PANE_KEY) emitReadingPane();
}

/**
 * One source for every reader of the setting: Settings writes it and Mail lays
 * itself out by it, and a `useState` per caller would let the two disagree
 * until a reload. Another tab changing it arrives as a `storage` event.
 */
function subscribeReadingPane(listener: () => void): () => void {
  readingPaneListeners.add(listener);
  if (readingPaneListeners.size === 1) window.addEventListener('storage', onReadingPaneStorage);
  return () => {
    readingPaneListeners.delete(listener);
    if (readingPaneListeners.size === 0) window.removeEventListener('storage', onReadingPaneStorage);
  };
}

/** Used only while storage is blocked, so the choice still applies until a reload. */
let readingPaneFallback: ReadingPanePosition | undefined;

function readingPaneSnapshot(): ReadingPanePosition {
  return readingPaneFallback ?? readReadingPane();
}

/**
 * Where the reading pane sits, per device (see the module comment for why this
 * one is not on the server). Mail reads it at desktop and tablet widths only;
 * a phone is one pane at a time whatever it says (`MailView`).
 */
export function useReadingPane(): [ReadingPanePosition, (next: ReadingPanePosition) => void] {
  const position = useSyncExternalStore(subscribeReadingPane, readingPaneSnapshot, readingPaneSnapshot);
  const update = useCallback((next: ReadingPanePosition) => {
    try {
      localStorage.setItem(READING_PANE_KEY, next);
      readingPaneFallback = undefined;
    } catch {
      // Storage blocked: the choice applies now and just won't survive a reload.
      readingPaneFallback = next;
    }
    emitReadingPane();
  }, []);
  return [position, update];
}

/* -------------------------------------------------------- default mail view */

/** Which list `/mail` opens on (ADR 0056 §7). */
export type DefaultMailView = 'account' | 'unified';

const DEFAULT_MAIL_VIEW_KEY = 'ols-mail.default-mail-view';
const DEFAULT_MAIL_VIEW_VALUES: readonly DefaultMailView[] = ['account', 'unified'];

function readDefaultMailView(): DefaultMailView {
  try {
    const value = localStorage.getItem(DEFAULT_MAIL_VIEW_KEY);
    return DEFAULT_MAIL_VIEW_VALUES.includes(value as DefaultMailView)
      ? (value as DefaultMailView)
      : 'account';
  } catch {
    return 'account';
  }
}

/**
 * Which list Mail opens on: the active mailbox, or All inboxes.
 *
 * **Per device**, like the reading-pane position and unlike the signature. It
 * describes the screen you are sitting at — one mailbox on a work phone, all of
 * them on the desktop are both ordinary — and a `UserPreferences` field would
 * put a choice with no cross-device meaning on the wire, where it would need a
 * Go struct field and a `DisallowUnknownFields` lockstep to get there.
 *
 * `'account'` is the default: a unified list is never what someone gets without
 * asking for it (ADR 0056 §7).
 */
export function useDefaultMailView(): [DefaultMailView, (next: DefaultMailView) => void] {
  const [view, setView] = useState<DefaultMailView>(readDefaultMailView);
  const update = useCallback((next: DefaultMailView) => {
    setView(next);
    try {
      localStorage.setItem(DEFAULT_MAIL_VIEW_KEY, next);
    } catch {
      /* storage blocked — the choice just won't survive a reload */
    }
  }, []);
  return [view, update];
}

/** The same value without subscribing; for the one-shot redirect on `/mail`. */
export function defaultMailView(): DefaultMailView {
  return readDefaultMailView();
}

/**
 * History state marking a navigation as "the mailbox the user just picked".
 *
 * The setting above redirects bare `/mail` to the merged list, which is right
 * when Mail is being *opened* and wrong the instant someone picks one mailbox
 * out of the switcher: the redirect would put them straight back into the list
 * they were trying to leave, and the switcher would look broken. History state
 * rather than a query parameter, because it describes one navigation and has no
 * business surviving a bookmark.
 */
export const PICKED_MAILBOX_STATE = { pickedMailbox: true } as const;

export function isPickedMailbox(state: unknown): boolean {
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as { pickedMailbox?: unknown }).pickedMailbox === true
  );
}
